import { useEffect, useState } from "react";
import { Plus, Trash2, BookText, Scale, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyLine = () => ({ account_id: "", debit: "", credit: "" });

export default function Journal() {
  const { currentId } = useCompany();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);

  const load = () => api.get("/journal").then((r) => setEntries(r.data));
  useEffect(() => {
    if (!currentId) return;
    load();
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, [currentId]);

  const setLine = (i, key, val) => {
    const l = [...lines];
    l[i] = { ...l[i], [key]: val };
    setLines(l);
  };
  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const openNew = () => {
    setEditId(null);
    setDate(todayISO());
    setMemo("");
    setLines([emptyLine(), emptyLine()]);
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditId(e.id);
    setDate(e.date);
    setMemo(e.memo);
    setLines(e.lines.map((l) => ({
      account_id: l.account_id,
      debit: l.debit ? String(l.debit) : "",
      credit: l.credit ? String(l.credit) : "",
    })));
    setOpen(true);
  };

  const save = async () => {
    if (!memo.trim()) return toast.error("Add a memo");
    const valid = lines.filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit)));
    if (valid.length < 2) return toast.error("Add at least two account lines");
    if (!balanced) return toast.error("Debits must equal credits");
    const payload = {
      date,
      memo,
      lines: valid.map((l) => ({
        account_id: l.account_id,
        debit: parseFloat(l.debit) || 0,
        credit: parseFloat(l.credit) || 0,
      })),
    };
    try {
      if (editId) await api.put(`/journal/${editId}`, payload);
      else await api.post("/journal", payload);
      toast.success(editId ? "Journal entry updated" : "Journal entry posted");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save entry");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/journal/${id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot delete");
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Journal Entries</h1>
          <p className="text-slate-500 text-sm mt-1">Every posting in the general journal (auto + manual).</p>
        </div>
        <Button data-testid="add-journal-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> Manual Entry
        </Button>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {entries.length === 0 ? (
          <div className="p-16 text-center">
            <BookText className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No journal entries yet. They appear as you record transactions.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="journal-list">
            {entries.map((e) => (
              <div key={e.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-900">{fmtDate(e.date)}</span>
                    <span className="text-sm text-slate-600">{e.memo}</span>
                    <Badge variant="outline" className="capitalize bg-slate-50 text-slate-500 border-slate-200">
                      {e.source_type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm tabular-nums text-slate-500">{money(e.debit_total)}</span>
                    {e.source_type === "manual" && (
                      <Button variant="ghost" size="icon" data-testid={`delete-journal-${e.id}`} onClick={() => remove(e.id)}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    )}
                  </div>
                </div>
                <table className="w-full text-sm mt-2">
                  <tbody>
                    {e.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="py-1 pl-4 text-slate-400 tabular-nums w-20">{l.account_code}</td>
                        <td className="py-1 text-slate-700">{l.account_name}</td>
                        <td className="py-1 text-right tabular-nums w-28 text-slate-900">{l.debit ? money(l.debit) : ""}</td>
                        <td className="py-1 text-right tabular-nums w-28 text-slate-900">{l.credit ? money(l.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">New Manual Journal Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date</Label>
                <div className="mt-1">
                  <DatePicker testId="journal-date-picker" value={date} onChange={setDate} />
                </div>
              </div>
              <div className="col-span-2">
                <Label>Memo</Label>
                <Input data-testid="journal-memo-input" value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1 rounded-md" placeholder="e.g. Depreciation for Q1" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 font-medium">Account</th>
                    <th className="px-3 py-2 font-medium w-28 text-right">Debit</th>
                    <th className="px-3 py-2 font-medium w-28 text-right">Credit</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-3 py-2">
                        <Select value={l.account_id} onValueChange={(v) => setLine(i, "account_id", v)}>
                          <SelectTrigger data-testid={`journal-account-${i}`} className="rounded-md border-slate-200">
                            <SelectValue placeholder="Select account" />
                          </SelectTrigger>
                          <SelectContent className="bg-white max-h-72">
                            {accounts.map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.code} · {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Input type="number" step="0.01" data-testid={`journal-debit-${i}`} value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} className="rounded-md border-slate-200 text-right tabular-nums" />
                      </td>
                      <td className="px-3 py-2">
                        <Input type="number" step="0.01" data-testid={`journal-credit-${i}`} value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} className="rounded-md border-slate-200 text-right tabular-nums" />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {lines.length > 2 && (
                          <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" data-testid="add-journal-line-btn" onClick={() => setLines([...lines, emptyLine()])} className="rounded-md">
                <Plus className="h-4 w-4 mr-1" /> Add line
              </Button>
              <div className={`flex items-center gap-2 text-sm ${balanced ? "text-emerald-600" : "text-rose-600"}`}>
                <Scale className="h-4 w-4" />
                <span className="tabular-nums">Dr {money(totalDebit)} · Cr {money(totalCredit)}</span>
                <span>{balanced ? "Balanced" : "Out of balance"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="post-journal-btn" onClick={save} disabled={!balanced} className="rounded-md bg-slate-900 hover:bg-slate-800">Post Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
